import { useTranslate } from "@refinedev/core";
import { Flex, Input, Select, Typography, UploadFile } from "antd";
import { Dispatch, SetStateAction } from "react";
import { StagedAsset } from "components/uploader/types";

type AssetMetaFieldsProps = {
  file: UploadFile;
  stagedItems?: StagedAsset;
  setStagedItems?: Dispatch<SetStateAction<StagedAsset>>;
};

/**
 * Per-file description & tags editor rendered under each upload preview.
 * Values are stored in the shared staged-assets state keyed by file uid,
 * from where `useUploadFiles` merges them into each asset's create request.
 */
export const AssetMetaFields = ({
  file,
  stagedItems,
  setStagedItems,
}: AssetMetaFieldsProps) => {
  const t = useTranslate();
  const staged = stagedItems?.find((item) => item.uid === file.uid);
  const description = staged?.description ?? file.name;
  const tags = staged?.tags ?? [];

  const updateStaged = (patch: Partial<StagedAsset[number]>) => {
    setStagedItems?.((prev) => {
      const list = prev ?? [];
      if (list.some((item) => item.uid === file.uid)) {
        return list.map((item) =>
          item.uid === file.uid ? { ...item, ...patch } : item
        );
      }
      return [...list, { uid: file.uid, ...patch }];
    });
  };

  return (
    <Flex vertical gap={8} style={{ width: "100%" }}>
      <Flex vertical gap={2} style={{ width: "100%" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("asset_libraries.upload.name_label")}
        </Text>
        <Input
          size="small"
          value={description}
          onChange={(e) => updateStaged({ description: e.target.value })}
          placeholder={t("asset_libraries.upload.description_placeholder")}
        />
      </Flex>
      <Flex vertical gap={2} style={{ width: "100%" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("asset_libraries.upload.tags_label")}
        </Text>
        <Select
          size="small"
          mode="tags"
          value={tags}
          onChange={(value: string[]) => updateStaged({ tags: value })}
          placeholder={t("asset_libraries.upload.tags_placeholder")}
          style={{ width: "100%" }}
          tokenSeparators={[","]}
          open={false}
          suffixIcon={null}
        />
      </Flex>
    </Flex>
  );
};

const { Text } = Typography;
